import { customInstance } from '@/common/infrastructure/http/apiClient';
import { serializeBlockJson } from '@/blocks/application/mappers/blockMapper';
import { toBlockLifecycleStatus } from '@/blocks/application/sieve/buildBlocksSieveFilter';
import type { BlockContent, BlockKind, BlockStyle } from '@/blocks/domain/types';
import type { CreatePdfBlockVersionInput } from '@/generated/models/createPdfBlockVersionInput';
import type { UpdatePdfBlockVersionInput } from '@/generated/models/updatePdfBlockVersionInput';

export interface SavedBlockVersion {
  blockVersionId: number;
  versionNumber: number;
  status: string;
  blockJson: string;
}

export interface SaveBlockDraftArgs {
  blockId: string;
  kind: BlockKind;
  /** The kind as stored on the API. The header PUT sends it back unchanged so a legacy `project_details` is never rewritten. */
  rawKind?: string;
  content: BlockContent;
  style: BlockStyle;
  /**
   * Block header (PUT `/blocks/{id}`). The name is header-level, so it goes live on Save. Callers
   * pass `name`/`description` only when one of them changed — nothing is sent otherwise.
   */
  name?: string;
  description?: string | null;
  changeNotes?: string;
  /** When set, overwrite this draft. A second POST while a draft is open is a 409. */
  draftVersionId?: string | null;
  /**
   * The Published version this draft was restored from. ptb's draft PUT is a full replace, so a PUT
   * that omits it clears it — callers carry the draft's current value forward on every write.
   * Send it only while that version is still Published (ptb rejects it otherwise: 400, or 404 for an archived source).
   */
  revertedFromVersionNumber?: number | null;
  /**
   * Draft POST only: the Published version whose content seeds the new draft. Must be Published and
   * must match `revertedFromVersionNumber` (ptb rejects it otherwise: 409, 400 or 404). Ignored on the PUT.
   */
  sourceVersionId?: number | null;
  /**
   * `false` writes no version body: a rename-only Save touches the header and nothing else, and a
   * Publish of a clean open draft promotes it as it is. A rename never mints a version (D-16).
   * Default `true`.
   */
  writeVersion?: boolean;
}

const versionsUrl = (blockId: string) => `/api/pdf-templates/v1/blocks/${blockId}/versions`;

/** The open draft's id as a trimmed string, or null when there is no open draft. */
export function openDraftIdOf(draftVersionId: string | null | undefined): string | null {
  const trimmed = draftVersionId?.trim();
  return trimmed ? trimmed : null;
}

/** PUT `/blocks/{id}` — only when the caller supplied a name or description (i.e. one changed). */
export async function saveBlockHeader(
  args: Pick<SaveBlockDraftArgs, 'blockId' | 'kind' | 'rawKind' | 'name' | 'description'>
): Promise<void> {
  const { blockId, kind, rawKind, name, description } = args;
  if (name === undefined && description === undefined) return;
  // The kind and type are immutable on the API: send what is STORED, not what the editor resolved it to.
  const storedKind = rawKind?.trim() || kind;

  await customInstance({
    url: `/api/pdf-templates/v1/blocks/${blockId}`,
    method: 'PUT',
    data: {
      name: name ?? '',
      description: description ?? null,
      category: storedKind,
      blockType: storedKind
    }
  });
}

/**
 * Draft version write (PDF-1). The request bodies are typed by the generated
 * `CreatePdfBlockVersionInput` (`asDraft`, `sourceVersionId`, `revertedFromVersionNumber`) and
 * `UpdatePdfBlockVersionInput` (`revertedFromVersionNumber`); the version POST/PUT go through
 * `customInstance`. PUT when a draft is open, otherwise POST `asDraft: true`.
 */
export async function saveBlockDraftVersion(args: SaveBlockDraftArgs): Promise<SavedBlockVersion> {
  const { blockId, content, style, changeNotes, draftVersionId, revertedFromVersionNumber, sourceVersionId } = args;
  const blockJson = serializeBlockJson({ content, style });
  const openDraftId = openDraftIdOf(draftVersionId);

  if (openDraftId) {
    const body: UpdatePdfBlockVersionInput = {
      blockJson,
      ...(changeNotes !== undefined ? { changeNotes } : {}),
      ...(revertedFromVersionNumber != null ? { revertedFromVersionNumber } : {})
    };
    return customInstance<SavedBlockVersion>({
      url: `${versionsUrl(blockId)}/${openDraftId}`,
      method: 'PUT',
      data: body
    });
  }

  const body: CreatePdfBlockVersionInput = {
    blockId: Number(blockId),
    blockJson,
    asDraft: true,
    ...(changeNotes !== undefined ? { changeNotes } : {}),
    ...(revertedFromVersionNumber != null ? { revertedFromVersionNumber } : {}),
    ...(revertedFromVersionNumber != null && sourceVersionId != null ? { sourceVersionId } : {})
  };
  return customInstance<SavedBlockVersion>({
    url: versionsUrl(blockId),
    method: 'POST',
    data: body
  });
}

/** Header (when it changed), then the draft version — unless `writeVersion: false`, which resolves `null`. */
export async function saveBlockDraft(args: SaveBlockDraftArgs): Promise<SavedBlockVersion | null> {
  await saveBlockHeader(args);
  if (args.writeVersion === false) return null;
  return saveBlockDraftVersion(args);
}

export async function publishSavedBlockDraft(blockId: string, blockVersionId: number): Promise<void> {
  await customInstance({
    url: `${versionsUrl(blockId)}/${blockVersionId}/publish`,
    method: 'POST'
  });
}

/** True when a wire status names a Published version (trimmed and case-insensitive, like the rest of the editor). */
export function isPublishedStatus(status: string | null | undefined): boolean {
  return toBlockLifecycleStatus(status) === 'Published';
}
